import {
  DefaultButton,
  DialogActions,
  DialogContent,
  DialogContentText,
  PrimaryButton,
  TextField,
  Typography,
} from '@bb-ui/react-library';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { useEnqueueBbSnackbar } from '@bb-ui/react-library/dist/components/Snackbar/SnackbarContent';
import { Switch } from '@bb-ui/react-library/dist/components/Switch';
import useRestApi from 'hooks/useRestApi';
import type { SnackbarKey } from 'notistack';
import { useSnackbar } from 'hooks/useSnackbar';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { apiUrl } from 'utils/apiUrl';
import { JobIds, JobRequest } from 'pages/ReleaseStages/ReleaseStages.types';

export interface ReleaseStageUpgradeButtonDialogProps extends DialogProps {
  onClose: () => void;
  releaseStageId: string;
  releaseStageName: string;
  onSuccess: () => void;
  initialInstallationList?: string[];
  /** We still use tenant IDs to re-run failed upgrades because those are gathered from failed child executions, which only expose tenantId. */
  initialTenantList?: string[];
}

export const parseSiteList = (value: string): string[] =>
  Array.from(new Set(value.split(/[\s,;]+/).map((siteId) => siteId.trim()))).filter(Boolean);

export const toLocalDateTimeInputValue = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export const ReleaseStageUpgradeButtonDialog: React.FunctionComponent<
  ReleaseStageUpgradeButtonDialogProps
> = (props) => {
  const {
    id,
    open,
    onClose,
    releaseStageId,
    releaseStageName,
    onSuccess,
    initialInstallationList,
    initialTenantList,
  } = props;
  const { t } = useTranslation();
  const [snackbarKey, setSnackbarKey] = React.useState<SnackbarKey | null>(null);
  const usesTenantScope = initialTenantList !== undefined;
  const initialSiteList = usesTenantScope ? initialTenantList : initialInstallationList;
  const [siteListText, setSiteListText] = React.useState(() => initialSiteList?.join('\n') ?? '');
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [scheduledDateTime, setScheduledDateTime] = React.useState('');
  const [scheduleTouched, setScheduleTouched] = React.useState(false);
  const [scheduleSubmitInvalid, setScheduleSubmitInvalid] = React.useState(false);
  const scheduledStartTime = React.useMemo(() => {
    if (!scheduleEnabled || !scheduledDateTime) return undefined;
    const date = new Date(scheduledDateTime);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }, [scheduleEnabled, scheduledDateTime]);
  const scheduleInvalid =
    scheduleEnabled && (!scheduledStartTime || Date.parse(scheduledStartTime) <= Date.now());
  const resetForm = React.useCallback(() => {
    setSiteListText(initialSiteList?.join('\n') ?? '');
    setScheduleEnabled(false);
    setScheduledDateTime('');
    setScheduleTouched(false);
    setScheduleSubmitInvalid(false);
  }, [initialSiteList]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const {
    failedRequests,
    succeededRequests,
    clearFailedRequests,
    clearSucceededRequests,
    doPost,
    loadingRequests,
  } = useRestApi(apiUrl('lct', `releaseStageJob/${releaseStageId}/triggerCustomBatch`), {
    manual: true,
  });
  const dismissSnackbar = React.useCallback(() => {
    if (snackbarKey) {
      closeSnackbar(snackbarKey);
      setSnackbarKey(null);
    }
  }, [closeSnackbar, snackbarKey]);

  React.useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const siteList = React.useMemo(() => parseSiteList(siteListText), [siteListText]);

  const submitPost = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (scheduleEnabled && (!scheduledStartTime || Date.parse(scheduledStartTime) <= Date.now())) {
      setScheduleTouched(true);
      setScheduleSubmitInvalid(true);
      return;
    }
    setSnackbarKey(enqueueSnackbar('Submitting Execution...', { variant: 'info' }));
    const body: JobRequest = {
      jobId: JobIds.UPGRADE,
      ...(siteList.length > 0 &&
        (usesTenantScope ? { tenantList: siteList } : { installationList: siteList })),
      ...(scheduledStartTime && {
        scheduledStartTime,
      }),
    };
    doPost(body);
  };

  const closeDialog = () => {
    resetForm();
    onClose();
  };

  React.useEffect(() => {
    if (failedRequests.length > 0) {
      onSuccess();
      clearFailedRequests();
      dismissSnackbar();
      setSnackbarKey(enqueueSnackbar('Error submitting execution', { variant: 'error' }));
    }
  }, [
    clearFailedRequests,
    closeSnackbar,
    dismissSnackbar,
    enqueueSnackbar,
    failedRequests,
    onSuccess,
  ]);

  const showSuccessSnackbar = useEnqueueBbSnackbar(
    { snackbarMessage: 'Execution Submitted' },
    {
      actionMessage: 'View Details',
      onActionClick: () => {
        const result = succeededRequests.at(0);
        const tenantId: string | undefined = result?.response?.data?.tenantId;
        const executionId: string | undefined = result?.response?.data?.executionId;
        if (tenantId && executionId) {
          history.push(`/tenants/${tenantId}/executions/${executionId}`);
          closeSnackbar();
        }
      },
      closeButtonAriaLabel: 'close',
    },
  );
  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      resetForm();
      onSuccess();
      dismissSnackbar();
      showSuccessSnackbar();
      clearSucceededRequests();
    }
  }, [
    clearSucceededRequests,
    dismissSnackbar,
    onSuccess,
    resetForm,
    showSuccessSnackbar,
    succeededRequests.length,
  ]);

  return (
    <Dialog
      id={id}
      open={open}
      onClose={closeDialog}
      data-testid={id}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
    >
      <DialogTitle onClose={closeDialog} id={`${id}-title`} closeLabel={t('global.close')}>
        {t('releaseStages.releaseStageUpgradeDialog.title')}
      </DialogTitle>
      <form onSubmit={submitPost} noValidate data-testid="release-stage-upgrade-button-form">
        <DialogContent>
          <DialogContentText
            id={`${id}-release-stage-upgrade-button`}
            data-testid={`${id}-release-stage-upgrade-content-text`}
          >
            {t('releaseStages.releaseStageUpgradeDialog.content', {
              ReleaseStageName: releaseStageName,
            })}
          </DialogContentText>
          <FormControlLabel
            control={
              <Switch
                id={`${id}-schedule-toggle`}
                checked={scheduleEnabled}
                onChange={() => {
                  const enabled = !scheduleEnabled;
                  setScheduleEnabled(enabled);
                  if (!enabled) {
                    setScheduledDateTime('');
                    setScheduleTouched(false);
                    setScheduleSubmitInvalid(false);
                  }
                }}
              />
            }
            label={t('jobService.scheduleForLater')}
          />
          {scheduleEnabled && (
            <TextField
              id={`${id}-scheduled-start-time`}
              label={t('jobService.scheduledStartTime')}
              type="datetime-local"
              value={scheduledDateTime}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setScheduledDateTime(event.target.value);
                setScheduleTouched(true);
                setScheduleSubmitInvalid(false);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: toLocalDateTimeInputValue(new Date()) }}
              error={(scheduleInvalid && scheduleTouched) || scheduleSubmitInvalid}
              helperText={
                scheduleInvalid || scheduleSubmitInvalid
                  ? t('releaseStages.releaseStageUpgradeDialog.futureScheduleRequired')
                  : undefined
              }
              fullWidth
            />
          )}
          <TextField
            id={`${id}-site-list`}
            label={t(
              usesTenantScope
                ? 'releaseStages.releaseStageUpgradeDialog.tenantListLabel'
                : 'releaseStages.releaseStageUpgradeDialog.installationListLabel',
            )}
            value={siteListText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSiteListText(event.target.value)
            }
            fullWidth
            multiline
            minRows={6}
            helperText={t(
              usesTenantScope
                ? 'releaseStages.releaseStageUpgradeDialog.tenantListHelper'
                : 'releaseStages.releaseStageUpgradeDialog.installationListHelper',
            )}
          />
          <Typography variant="body2">
            {siteList.length > 0
              ? t('releaseStages.releaseStageUpgradeDialog.selectedTenantCount', {
                  count: siteList.length,
                })
              : t('releaseStages.releaseStageUpgradeDialog.allTenantsWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <PrimaryButton type="submit" disabled={loadingRequests || scheduleInvalid}>
            {t('releaseStages.releaseStageUpgradeDialog.confirmUpgradeTenantsFromReleaseStage')}
          </PrimaryButton>
          <DefaultButton onClick={closeDialog}>
            {t('releaseStages.releaseStageUpgradeDialog.cancelUpgradeTenantsFromReleaseStage')}
          </DefaultButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
